'use client';

import type {FC} from 'react';
import {useEffect, useState} from 'react';
import clsx from 'clsx';
import {usePathname, useSearchParams} from 'next/navigation';

/**
 * Shows a progress bar at the top of the page while a link click loads another page.
 *
 * Pages wait for their data before responding, so they can answer missing content with a real
 * 404 status. Route-level loading.tsx files would stream a 200 response before the page knows
 * whether its content exists, so this bar gives navigation feedback instead.
 */
export const NavigationProgress: FC = () => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const url = `${pathname}?${searchParams.toString()}`;

	// The URL the user navigated away from. The navigation is done once the URL changes.
	const [pendingFrom, setPendingFrom] = useState<string | null>(null);
	if (pendingFrom !== null && pendingFrom !== url) {
		setPendingFrom(null);
	}

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (isPageNavigation(event)) {
				setPendingFrom(url);
			}
		};

		document.addEventListener('click', handleClick, {capture: true});
		return () => document.removeEventListener('click', handleClick, {capture: true});
	}, [url]);

	return (
		<div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-50 h-1">
			<div
				className={clsx(
					'bg-primary h-full',
					pendingFrom !== null
						? 'animate-navigation-progress'
						: 'w-full opacity-0 transition-opacity duration-300',
				)}
			/>
		</div>
	);
};

function isPageNavigation(event: MouseEvent) {
	if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
		return false;
	}

	const anchor = event.target instanceof Element ? event.target.closest('a') : null;
	if (
		!anchor ||
		anchor.hasAttribute('download') ||
		(anchor.target && anchor.target !== '_self')
	) {
		return false;
	}

	const destination = new URL(anchor.href, window.location.href);
	return (
		destination.origin === window.location.origin &&
		(destination.pathname !== window.location.pathname ||
			destination.search !== window.location.search)
	);
}
